import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Typography,
  Button
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SchoolAdminItem from '../SchoolAdminItem';
import AddAdmin from '../SchoolAddAdmin/AddAdmin';
import useAction from '../../../../utils/useAction';
import { SnackbarContext } from '../../../../state/SnackbarContextProvider';
import { getSchoolAdmins, removeSchoolAdmin } from '../../../../actions/user.actions';
import { selectSchoolAdmins, selectUser, selectSelectedSchoolId } from '../../../../reducers/user.selectors';
import { useSelector } from 'react-redux';
import { routes } from '../../../../constants';
import useRouter from '../../../../utils/useRouter';
import { useTranslation } from 'react-i18next';
import { SchoolAdmin } from '@kimuno/api-sdk/lib/types/responses/Teacher';
import { palette } from '../../../../assets/styles/themes/tokens';

const SchoolAdminsList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const { openSnackbar } = useContext(SnackbarContext);
  const schoolAdmins: SchoolAdmin[] = useSelector(selectSchoolAdmins) || [];
  const getSchoolAdminsAction = useAction(getSchoolAdmins);
  const removeSchoolAdminAction = useAction(removeSchoolAdmin);
  const user = useSelector(selectUser);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);
  const { router } = useRouter();
  const { t } = useTranslation();
  const lastFetchedSchoolId = useRef<string | null>(null);

  useEffect(() => {
    if (user && !user.isSchoolAdmin) {
      router.push(`/${routes.dashboard.root}`);
    }
  }, [user?.isSchoolAdmin]);

  useEffect(() => {
    // Don't fetch if user is not a school admin
    if (!user || !user?.isSchoolAdmin) {
      return;
    }

    const schoolId = selectedSchoolId || user.manageableSchools?.[0]?.id;
    if (!schoolId) {
      openSnackbar({
        type: 'error',
        message: t('Error while fetching school admins')
      });
      setLoading(false);
      return;
    }

    // Prevent duplicate calls for the same school
    if (lastFetchedSchoolId.current === schoolId) {
      return;
    }
    lastFetchedSchoolId.current = schoolId;
    setLoading(true);
    getSchoolAdminsAction(schoolId).then((data) => {
      if (data?.result?.message) {
        openSnackbar({
          type: 'error',
          message: t('Error while fetching school admins')
        });
      }
    }).catch(() => {
      openSnackbar({
        type: 'error',
        message: t('Error while fetching school admins')
      });
    }).finally(() => setLoading(false));
  }, [user?.isSchoolAdmin, selectedSchoolId]);

  const handleAdminAdded = () => {
    setShowAddForm(false);
    // Refresh the school admins list
    if (selectedSchoolId) {
      lastFetchedSchoolId.current = null; // Reset to allow refresh
      setLoading(true);
      getSchoolAdminsAction(selectedSchoolId).finally(() => setLoading(false));
    }
  };  

  const handleDeleteAdmin = (adminId: string, schoolId?: string) => {
    if (!adminId || !schoolId) {
      openSnackbar({
        type: 'error',
        message: t('Error while deleting school admin')
      });

      return;
    }

    removeSchoolAdminAction(adminId, schoolId).then((result) => {
      if (result?.error) {
        openSnackbar({
          type: 'error',
          message: t('Error while deleting school admin')
        });
      } else {
        openSnackbar({
          type: 'success',
          message: t('School admin deleted successfully')
        });
  
        // Refresh the list after successful deletion
        lastFetchedSchoolId.current = null; // Reset to allow refresh
        setLoading(true);
        getSchoolAdminsAction(schoolId).finally(() => setLoading(false));
      }
    }).catch(() => {
      openSnackbar({
        type: 'error',
        message: t('Error while deleting school admin')
      });
    });
  };

  return (
    <Box className='root' paddingTop='10px' sx={{ minHeight: '100%', backgroundColor: 'rgb(249, 250, 251)' }}>
      <Box className='container'>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography
            variant='h5'
            sx={{ color: palette.text.primary }}
            marginBottom='10px'
            marginTop='10px'
          >
            {t('School Admins')}
          </Typography>
        </Box>
        <Box marginBottom={1} display='flex' justifyContent='flex-end'>
          <Button
            variant='outlined'
            startIcon={<AddCircleIcon />}
            onClick={() => setShowAddForm(true)}
          >
            {t('Add School Admin')}
          </Button>
        </Box>
        <AddAdmin
          onAdminAdded={handleAdminAdded}
          onClose={() => setShowAddForm(false)}
          open={showAddForm}
        />
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('Name').toUpperCase()}</TableCell>
                <TableCell>{t('Email').toUpperCase()}</TableCell>
                <TableCell>{t('Status').toUpperCase()}</TableCell>
                <TableCell sx={{ p: 0, width: 100, textAlign: 'center' }}>{t('Actions').toUpperCase()}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align='center'>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                schoolAdmins?.map((admin: SchoolAdmin) => (
                  <SchoolAdminItem
                    key={admin._id}
                    admin={admin}
                    onDelete={() => handleDeleteAdmin(admin._id, selectedSchoolId)} 
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SchoolAdminsList;
